/**
 * The plan editor's numbers: each item's facts today, a debt's rate schedule and what it does to the
 * payment, whose pay an employer match follows, tax relief defaults and the one-time note about rate
 * changes (TIMELINE R23, decisions 4 and 5). Every debt and investment is read from the decoded plan,
 * so a debt's balance day defaults to the setup day as the engine's own decode sets it.
 */
import {
  assets as assetsNs, core, debt as debtNs, formatDay, investments, parseDay, plan as planNs, schedule,
  type Asset, type Day, type Plan, type TaxAdvantagedAccount, type Transaction,
} from '@map-my-money/engine';
import { num, round1 } from './money.ts';
import { decode, withTypedDebts, type WaypointInput, type WirePlan } from './run.ts';

const round2 = (n: number): number => Math.round(n * 100) / 100;

const txOf = (plan: Plan, id: string): Transaction | null => plan.transactions.find((t) => t.id === id) ?? null;

// MARK: Item facts

export interface DebtFacts {
  readonly kind: 'debt';
  readonly enteredBy: 'balance' | 'loan';
  readonly balanceToday: number;
  /** The day the typed balance was true; null for a loan, or a balance with no day (then it is today's). */
  readonly balanceDate: Day | null;
  readonly payoff: Day | null;
}

export interface InvestmentFacts {
  readonly kind: 'investment';
  readonly valueToday: number;
}

export interface AssetFacts {
  readonly kind: 'asset';
  readonly valueToday: number;
  readonly price: number;
  /** The purchase day when it is after today: nothing counts before it exists (R23). */
  readonly plannedFrom: Day | null;
}

export type ItemFacts = DebtFacts | InvestmentFacts | AssetFacts | { readonly kind: 'flow' };

function debtFacts(t: Transaction, today: Day): DebtFacts {
  const enteredBy = t.debtEntryMode === 'currentBalance' && t.debtCurrentBalance !== null ? 'balance' : 'loan';
  return {
    kind: 'debt',
    enteredBy,
    balanceToday: Math.abs(num(debtNs.remainingBalance(t, today, today))),
    balanceDate: enteredBy === 'balance' ? t.debtBalanceDate : null,
    payoff: debtNs.payoffDate(t, today, today),
  };
}

const assetFacts = (a: Asset, today: Day): AssetFacts => ({
  kind: 'asset',
  valueToday: a.purchaseDate > today ? num(a.purchasePrice) : num(assetsNs.assetValue(a, today)),
  price: num(a.purchasePrice),
  plannedFrom: a.purchaseDate > today ? a.purchaseDate : null,
});

function txFacts(plan: Plan, t: Transaction, today: Day): ItemFacts {
  if (t.type === 'debt') return debtFacts(t, today);
  if (t.type === 'investment') {
    return {
      kind: 'investment',
      valueToday: num(investments.calculateCurrentValue(t, today, plan.configuration.expectedReturnPercent, planNs.activeOfType(plan, 'income'))),
    };
  }
  return { kind: 'flow' };
}

/** Every item's facts today, keyed as the editor keys its rows: `tx:<id>` and `asset:<id>`. */
export function planFacts(wire: WirePlan, today: Day, waypoints: readonly WaypointInput[] = []): ReadonlyMap<string, ItemFacts> {
  const plan = withTypedDebts(decode(wire), waypoints, formatDay(today));
  const facts = new Map<string, ItemFacts>();
  for (const t of plan.transactions) facts.set(`tx:${t.id}`, txFacts(plan, t, today));
  for (const a of plan.assets) facts.set(`asset:${a.id}`, assetFacts(a, today));
  return facts;
}

export const itemFacts = (wire: WirePlan, key: string, today: Day): ItemFacts | null => planFacts(wire, today).get(key) ?? null;

/** Monthly balance path from today to payoff (at most 50 years), for the payoff sparkline. */
export function debtPathOf(wire: WirePlan, id: string, today: Day): { date: Day; balance: number }[] {
  const t = txOf(decode(wire), id);
  if (t === null || t.type !== 'debt') return [];
  const end = debtNs.payoffDate(t, today, today);
  const months = end === null ? 600 : Math.max(1, core.monthsBetween(today, end));
  const step = Math.max(1, Math.ceil(months / 60));
  const out: { date: Day; balance: number }[] = [];
  for (let m = 0; m <= months; m += step) {
    const date = core.addMonths(today, m);
    out.push({ date, balance: Math.abs(num(debtNs.remainingBalance(t, date, today))) });
  }
  if (end !== null) {
    while (out.length > 1 && out[out.length - 1]!.date >= end) out.pop();
    out.push({ date: end, balance: 0 });
  }
  return out;
}

/** The planned assets Home lists apart from what is owned: bought after today. */
export function plannedAssets(wire: WirePlan, today: Day) {
  return decode(wire).assets
    .filter((a) => a.isActive && a.purchaseDate > today)
    .map((a) => ({ id: a.id, label: a.label, assetClass: a.assetClass, price: num(a.purchasePrice), purchaseDate: a.purchaseDate }));
}

export type PlannedAsset = ReturnType<typeof plannedAssets>[number];

// MARK: Rate changes (decision 4)

export interface RateScheduleView {
  readonly baseRate: number;
  /** Planned rates, by date. */
  readonly changes: readonly { readonly date: Day; readonly rate: number }[];
  /** The typed regular payment. */
  readonly payment: number;
  /** Where a change re-prices the payment: the first payment at the new rate and the payments either side. */
  readonly paymentChanges: readonly { readonly from: Day; readonly before: number; readonly after: number; readonly change: number }[];
  readonly payoff: Day | null;
}

const sortedChanges = (t: Transaction) => [...(t.interestRateChanges ?? [])].sort((a, b) => a.date - b.date);

/** A debt's rates over time and what they do to its payment and payoff (decision 4). */
export function rateSchedule(wire: WirePlan, id: string, today: Day): RateScheduleView | null {
  const t = txOf(decode(wire), id);
  if (t === null || t.type !== 'debt') return null;
  const changes = sortedChanges(t);
  const payoff = debtNs.payoffDate(t, today, today);
  const last = changes.at(-1)?.date ?? today;
  const dates = debtNs.debtPaymentDates(t, payoff === null ? core.addMonths(core.maxDay(last, today), 13) : core.addDays(payoff, 1));
  const paymentChanges = changes.flatMap((c) => {
    const before = dates.filter((d) => d < c.date).at(-1);
    const next = dates.find((d) => d >= c.date && d !== payoff);
    if (before === undefined || next === undefined) return [];
    const was = round2(num(debtNs.debtPayment(t, before, today)));
    const now = round2(num(debtNs.debtPayment(t, next, today)));
    return was === now ? [] : [{ from: next, before: was, after: now, change: round2(now - was) }];
  });
  return {
    baseRate: num(t.interestRate ?? core.ZERO),
    changes: changes.map((c) => ({ date: c.date, rate: num(c.newRate) })),
    payment: num(t.amount),
    paymentChanges,
    payoff,
  };
}

/**
 * The active debts whose stored rate changes move the forecast: a change to another rate dated on or
 * after the debt's first payment and before it is paid off.
 */
export function debtsWithRateChanges(wire: WirePlan, today: Day): { readonly id: string; readonly label: string }[] {
  return decode(wire).transactions
    .filter((t) => t.type === 'debt' && t.isActive && (t.interestRateChanges?.length ?? 0) > 0)
    .filter((t) => {
      const start = core.maxDay(t.startDate, debtNs.effectiveStartDate(t));
      const first = debtNs.debtPaymentDates(t, core.addMonths(start, 13))[0];
      if (first === undefined) return false;
      const payoff = debtNs.payoffDate(t, t.startDate, today);
      let rate = num(t.interestRate ?? core.ZERO);
      return sortedChanges(t).some((c) => {
        const moves = num(c.newRate) !== rate && c.date >= first && (payoff === null || c.date < payoff);
        rate = num(c.newRate);
        return moves;
      });
    })
    .map((t) => ({ id: t.id, label: t.label }));
}

export const rateChangesAffectForecast = (wire: WirePlan, today: Day): boolean => debtsWithRateChanges(wire, today).length > 0;

// MARK: Employer match (decision 5)

const isRecurring = (t: Transaction) => {
  const step = schedule.frequencyStep(t.frequency);
  return step !== null && step.count > 0;
};

const isPayingOn = (t: Transaction, day: Day) =>
  t.type === 'income' && t.isActive && isRecurring(t) && t.startDate <= day && (t.endDate === null || t.endDate >= day);

const monthlyPay = (t: Transaction, day: Day): number => num(schedule.monthlyEquivalent(t.frequency, schedule.amountOn(t, day)));

/** The income a match follows by default: the largest recurring income paying today. */
export function largestIncome(wire: WirePlan, today: Day): { readonly id: string; readonly label: string } | null {
  const best = decode(wire).transactions
    .filter((t) => isPayingOn(t, today))
    .reduce<Transaction | null>((top, t) => (top === null || monthlyPay(t, today) > monthlyPay(top, today) ? t : top), null);
  return best === null ? null : { id: best.id, label: best.label };
}

/**
 * The next day after `asOf` that the largest regular income pays, one starting soon included: when a
 * first check-in is worth doing (Start's balances step and Home name it). Null without regular pay.
 */
export function nextPayday(wire: WirePlan, asOf: string): string | null {
  const today = parseDay(asOf);
  const plan = decode(wire);
  const from = (t: Transaction) => (t.startDate > today ? t.startDate : today);
  const best = plan.transactions
    .filter((t) => t.type === 'income' && t.isActive && isRecurring(t) && (t.endDate === null || t.endDate > today))
    .reduce<Transaction | null>((top, t) => (top === null || monthlyPay(t, from(t)) > monthlyPay(top, from(top)) ? t : top), null);
  if (best === null) return null;
  const paidBy = schedule.countOccurrencesUpTo(best, today);
  for (let day = core.addDays(today, 1), last = core.addMonths(today, 13); day <= last; day = core.addDays(day, 1)) {
    if (schedule.countOccurrencesUpTo(best, day) > paidBy) return formatDay(day);
  }
  return null;
}

export interface MatchTerms {
  /** The person's own payment as a percent of the linked pay for the same period; null without pay. */
  readonly ownPercent: number | null;
  readonly matchPercent: number;
  /** The income the match follows (the chosen one, else the largest). */
  readonly income: { readonly id: string; readonly label: string } | null;
  /** True when no income is chosen and the match follows the largest. */
  readonly isDefault: boolean;
}

/** "You 5%, employer 5% of Sam’s pay": an investment's own share of the linked pay and its match. */
export function matchTerms(wire: WirePlan, id: string, today: Day): MatchTerms | null {
  const plan = decode(wire);
  const t = txOf(plan, id);
  if (t === null || t.type !== 'investment') return null;
  const chosen = t.employerMatchIncomeId === null ? null : txOf(plan, t.employerMatchIncomeId);
  const linked = chosen !== null && chosen.type === 'income' ? chosen : null;
  const fallback = largestIncome(wire, today);
  const income = linked ?? (fallback === null ? null : txOf(plan, fallback.id));
  const occurrences = schedule.annualOccurrences(t.frequency);
  const payPerPeriod = income === null || !isRecurring(t) ? 0 : (monthlyPay(income, today) * 12) / occurrences;
  return {
    ownPercent: payPerPeriod > 0 ? round1((num(schedule.amountOn(t, today)) / payPerPeriod) * 100) : null,
    matchPercent: num(t.employerMatch ?? core.ZERO),
    income: income === null ? null : { id: income.id, label: income.label },
    isDefault: linked === null,
  };
}

// MARK: Tax relief

export const TAX_ACCOUNTS: Readonly<Record<'uk' | 'us', readonly TaxAdvantagedAccount[]>> = {
  uk: ['isa_stocks', 'isa_cash', 'sipp', 'workplace_pension', 'brokerage'],
  us: ['401k_traditional', '401k_roth', 'ira_traditional', 'ira_roth', 'hsa', 'brokerage'],
};

/** The relief an account type adds to each payment when none is typed (percent). */
export const taxReliefDefault = (account: TaxAdvantagedAccount): number => num(planNs.defaultTaxRelief(account));
